import { useContext, useEffect, useState, useMemo } from "react";
import { createFilterOptions } from "@mui/material/Autocomplete";
import { buscarContasSpedListaPorCnpj } from "../../../services/api-relatorios.service";
import { Autocomplete, TextField, Chip, Box } from "@mui/material";
import { ExportRelatorioContext } from "../ExportRelatorioContext";

const SELECIONAR_TODOS = { id: "__SELECIONAR_TODOS__", detalhes: "Selecionar Todos" };

const ContasContabeisInput = () => {
  const { empresas, setValoresFiltros, idFiltroSavoContasContabeis, filtros } =
    useContext(ExportRelatorioContext);

  const [contas, setContas] = useState([]);
  const [contasSelecionadas, setContasSelecionadas] = useState([]);

  const cnpjAnchor = useMemo(() => {
    const id = empresas?.[0]?.id;
    if (id == null) return "";
    const d = String(id).replace(/\D/g, "");
    return d.length >= 11 && d.length <= 14 ? d : "";
  }, [empresas]);

  const keepSentinelsFirst = useMemo(() => {
    const baseFilter = createFilterOptions({ stringify: (option) => option?.detalhes || "" });
    return (options, params) => {
      const filtered = baseFilter(options, params);
      const isSentinel = (o) => o?.id === SELECIONAR_TODOS.id;
      const sentinels = options.filter(isSentinel);
      const rest = filtered.filter((o) => !isSentinel(o));
      return [...sentinels, ...rest];
    };
  }, []);

  const atualizarFiltroContas = (idsContas) => {
    const idsValidos = idsContas
      .filter(
        (id) =>
          id !== "__SELECIONAR_TODOS__" &&
          id !== "__SELECIONAR_RESULTADO__" &&
          id !== null &&
          id !== undefined &&
          id !== ""
      )
      .map((id) => String(id));

    setValoresFiltros((prev) => {
      const filtroExistente = prev.find(
        (opt) =>
          opt.nome === "Contas Contábeis" ||
          opt.variavel === "$F{contas_contabil}" ||
          opt.variavel === "$F{contas_contabeis}"
      );

      if (filtroExistente) {
        return prev.map((opt) =>
          opt.nome === "Contas Contábeis" ||
          opt.variavel === "$F{contas_contabil}" ||
          opt.variavel === "$F{contas_contabeis}"
            ? { ...opt, valor: idsValidos }
            : opt
        );
      }

      const filtroTemplate = filtros?.find(
        (f) =>
          f.nome === "Contas Contábeis" ||
          f.variavel === "$F{contas_contabil}" ||
          f.variavel === "$F{contas_contabeis}"
      );

      if (filtroTemplate) {
        return [...prev, { ...filtroTemplate, valor: idsValidos }];
      }

      return [
        ...prev,
        {
          nome: "Contas Contábeis",
          variavel: "$F{contas_contabeis}",
          script: "$P{contas_contabeis}",
          valor: idsValidos,
          tipoDado: "autocomplete",
        },
      ];
    });
  };

  const montaOptions = async (cnpj) => {
    const res = await buscarContasSpedListaPorCnpj(cnpj);
    const list = Array.isArray(res?.contas) ? res.contas : [];
    return list.filter((c) => c?.id);
  };

  useEffect(() => {
    if (!cnpjAnchor) {
      setContas([]);
      setContasSelecionadas([]);
      return;
    }

    const fetchData = async () => {
      const optionListaContas = await montaOptions(cnpjAnchor);
      setContas([SELECIONAR_TODOS, ...optionListaContas]);

      if (idFiltroSavoContasContabeis.length > 0) {
        const idsSalvosStr = idFiltroSavoContasContabeis.map((id) => String(id));
        const valueEditFilter = optionListaContas.filter((c) => idsSalvosStr.includes(String(c.id)));
        const todasSelecionadas = valueEditFilter.length === optionListaContas.length;
        if (valueEditFilter.length > 0) {
          setContasSelecionadas(
            todasSelecionadas ? [SELECIONAR_TODOS, ...valueEditFilter] : valueEditFilter
          );
          atualizarFiltroContas(valueEditFilter.map((c) => c.id));
        } else {
          setContasSelecionadas([]);
          atualizarFiltroContas([]);
        }
      } else {
        setContasSelecionadas([]);
        atualizarFiltroContas([]);
      }
    };

    fetchData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [cnpjAnchor, idFiltroSavoContasContabeis]);

  const handleAutocompleteChange = (value) => {
    if (!value) {
      setContasSelecionadas([]);
      atualizarFiltroContas([]);
      return;
    }

    const selecionarTodos = value.some((v) => v.id === SELECIONAR_TODOS.id);
    const contasReais = contas.filter((c) => c.id !== SELECIONAR_TODOS.id);

    if (selecionarTodos) {
      const contasSelecionadasAtuais = contasSelecionadas.filter((c) => c.id !== SELECIONAR_TODOS.id);
      const todosSelecionados = contasSelecionadasAtuais.length === contasReais.length;

      if (todosSelecionados) {
        setContasSelecionadas([]);
        atualizarFiltroContas([]);
      } else {
        setContasSelecionadas([SELECIONAR_TODOS, ...contasReais]);
        atualizarFiltroContas(contasReais.map((c) => c.id));
      }
      return;
    }

    const contasSelecionadasReais = value.filter((c) => c.id !== SELECIONAR_TODOS.id);
    setContasSelecionadas(contasSelecionadasReais);
    atualizarFiltroContas(contasSelecionadasReais.map((c) => c.id));
  };

  const renderTags = (tagValue, getTagProps) => {
    const contasReais = tagValue.filter((c) => c.id !== SELECIONAR_TODOS.id);

    if (contasReais.length > 5) {
      return (
        <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.5, maxHeight: 80, overflow: "auto" }}>
          {contasReais.slice(0, 3).map((option, index) => (
            <Chip {...getTagProps({ index })} key={option.id} label={option.detalhes} size="small" />
          ))}
          <Chip label={`+${contasReais.length - 3} mais`} size="small" variant="outlined" />
        </Box>
      );
    }

    return contasReais.map((option, index) => (
      <Chip {...getTagProps({ index })} key={option.id} label={option.detalhes} size="small" />
    ));
  };

  return (
    <Autocomplete
      multiple
      options={contas}
      filterOptions={keepSentinelsFirst}
      value={contasSelecionadas}
      disableCloseOnSelect
      getOptionLabel={(option) => option.detalhes}
      noOptionsText="Nenhuma conta encontrada."
      isOptionEqualToValue={(option, value) => option.id === value.id}
      sx={{ marginTop: "1rem", width: "100%" }}
      onChange={(_, value) => handleAutocompleteChange(value)}
      renderTags={renderTags}
      renderInput={(params) => <TextField {...params} label="Contas Contábeis" />}
    />
  );
};

export default ContasContabeisInput;
